import { CSSProperties, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useMutation } from '@apollo/client';
import classNames from 'classnames';
import pick from 'lodash/pick';
import SearchIcon from '@mui/icons-material/Search';
import EditIcon from '@mui/icons-material/Edit';
import UnmuteIcon from '@mui/icons-material/VolumeOff';

import * as K from 'common/constants';
import ELEMENT, { createTestId } from 'common/element';
import { HELP_ROUTE } from 'components/help/constants';
import { UrlParams } from 'common/types';
import { AlertInfo, AlertInput, AlertStatus } from 'common/graphqlTypes';
import { buildUrl } from 'common/util/location';
import CheckboxRadio from 'components/common/CheckboxRadio/CheckboxRadio';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import TriggerHistoryGraph from 'components/common/Graphs/TriggerHistoryGraph';
import SaveAlertDialog from 'components/alerts/SaveAlertDialog';
import IconButton from 'components/common/IconButton/IconButton';
import Link from 'components/common/Link/Link';
import { AlertModel } from 'components/alerts/AlertListPage/useAlerts';
import { isTemplatedAlert } from 'components/alerts/alert';
import { ALERTS_QUERY, CREATE_ALERT, UNSILENCE_ALERTS_MUTATION, UPDATE_ALERT } from 'components/alerts/queries';
import useAlertPageAlerts from 'components/alerts/useAlertPageAlerts';
import { timezoneMoment } from 'common/util/time';

import './AlertListItem.scss';

/**
 * An alert list item component.
 *
 * @prop  alertModel              The alert Model that manages alert states
 * @prop  alert                   The alert list item object.
 * @prop  style                   The style object passed by react-window.
 * @prop  index                   Index of alert within containing list.
 */

type Alert = AlertInfo & {
  description: string;
  id: string;
  selected: boolean;
};

type Props = {
  alertModel: AlertModel;
  alert: Alert;
  style?: CSSProperties;
  index?: number;
};

function AlertListItem({ alertModel, alert, style, index }: Props): JSX.Element {
  const [showSaveAlertDialog, setShowSaveAlertDialog] = useState(false);
  const timezone = useRecoilValue(timezoneState);
  const [unSilenceAlerts] = useMutation(UNSILENCE_ALERTS_MUTATION, {
    refetchQueries: [{ query: ALERTS_QUERY }],
  });
  const [updateAlert] = useMutation(UPDATE_ALERT, {
    refetchQueries: [{ query: ALERTS_QUERY }],
  });
  const [createAlert] = useMutation(CREATE_ALERT, {
    refetchQueries: [{ query: ALERTS_QUERY }],
  });
  const alertPageAlerts = useAlertPageAlerts();
  const isIncompleteAlert = alert.history?.some((status) => status === AlertStatus.INCOMPLETE);

  const alertDetailLink = '/alert?id=' + alert.alertId;

  const handleToggleCheckbox = (): void => {
    alertModel.handleSelectAlert(alert.alertId);
  };

  const closeEditAlertDialog = (): void => {
    setShowSaveAlertDialog(false);
  };

  const openEditAlertDialog = (): void => {
    setShowSaveAlertDialog(true);
  };

  const handleEditAlert = (editedAlert: AlertInput): void => {
    const id = (alert as AlertInfo).alertId;
    updateAlert({
      variables: {
        alert: pick(editedAlert, [
          'alertAddresses',
          'description',
          'gracePeriod',
          'renotifyPeriod',
          'resolutionDelay',
          'trigger',
        ]),
        id,
      },
    })
      .then(closeEditAlertDialog)
      .then(alertPageAlerts.showUpdateSuccess)
      .catch(alertPageAlerts.showError);
  };

  const handleSaveAsNewAlert = (alertToSave: AlertInput): void => {
    createAlert({ variables: { alert: alertToSave } })
      .then(closeEditAlertDialog)
      .then(alertPageAlerts.showUpdateSuccess)
      .catch(alertPageAlerts.showError);
  };

  const handleUnmuteAlert = (): void => {
    const ids = [alert.alertId];
    unSilenceAlerts({ variables: { ids } })
      .then(alertModel.unselectAlerts)
      .then(alertPageAlerts.showUnMuteSuccess)
      .catch(alertPageAlerts.showError);
  };

  const filter = alert.parsedTriggerExpressions?.[0]?.filter || '';
  const query = alert.parsedPowerQueryExpressions?.[0]?.powerQuery || '';
  let searchLink = '';
  if (query) {
    // use timeOffset from alert trigger as our time range for PQ alert.
    const startTime = alert.trigger.split(':')?.[1].split('(')?.[0] || '1h';
    searchLink = buildUrl(K.ROUTE.POWER_QUERY, {
      [K.URL_PARAM.FILTER]: query,
      [K.URL_PARAM.START_TIME]: startTime,
    } as UrlParams);
  } else {
    const startTime = timezoneMoment(timezone)().subtract(1, 'hour').format('x');
    const endTime = timezoneMoment(timezone)().format('x');
    searchLink = buildUrl(K.ROUTE.EVENTS, {
      [K.URL_PARAM.FILTER]: filter,
      [K.URL_PARAM.START_TIME]: startTime,
      [K.URL_PARAM.END_TIME]: endTime,
    } as UrlParams);
  }

  const SearchLinkWithTooltip = (
    <Link href={searchLink} className="AlertListItem__SearchLink">
      <IconButton
        className="AlertListItem__SearchIcon"
        tooltip="View in Search"
        icon={<SearchIcon />}
        testId={ELEMENT.alertSearchIconButton}
      />
    </Link>
  );

  const unMuteIconWithTooltip = (
    <IconButton
      className="AlertListItem__UnmuteIconButton"
      tooltip="Muted - Click to unmute"
      onClick={handleUnmuteAlert}
      icon={<UnmuteIcon />}
      testId={ELEMENT.alertUnmuteIconButton}
    />
  );

  const editIconClassNames = classNames('AlertListItem__EditIcon', {
    'AlertListItem__EditIcon--disabled': isTemplatedAlert(alert),
  });
  const EditIconWithTooltip = (
    <IconButton
      className={editIconClassNames}
      tooltip="Edit"
      onClick={openEditAlertDialog}
      icon={<EditIcon />}
      testId={ELEMENT.alertEditIconButton}
    />
  );

  const triggerChartClass = classNames('AlertListItem__TriggeredChart', {
    'AlertListItem__TriggeredChart--Incomplete': isIncompleteAlert,
  });

  return (
    <div className="AlertListItem" style={style} data-test-id={createTestId(ELEMENT.alertListItem, index)}>
      <CheckboxRadio
        testId={createTestId(ELEMENT.alertListItemCheckbox, index)}
        type="checkbox"
        checked={alert.selected}
        onChange={handleToggleCheckbox}
      />
      <div className="AlertListItem__AlertTitle">
        <Link className="link" testId={createTestId(ELEMENT.alertTitleDetailsLink, index)} href={alertDetailLink}>
          {alert.description || alert.trigger}
        </Link>
      </div>
      <div className="AlertListItem__UnmuteIcon">{alert.silenced && unMuteIconWithTooltip}</div>
      <div className={triggerChartClass}>
        <TriggerHistoryGraph triggerHistory={alert.history} />
        {isIncompleteAlert && (
          <div className="AlertListItem__IncompleteWarning">
            <div className="AlertListItem__Marker">●</div>
            <span className="AlertListItem__IncompleteMessage">Alert is incomplete due to memory limits.</span>
            <a href={HELP_ROUTE.HELP_POWER_QUERIES_MEMORY_LIMIT}> More Info</a>
          </div>
        )}
      </div>
      <div className="AlertListItem__ActionIcons">
        {SearchLinkWithTooltip}
        {EditIconWithTooltip}
      </div>
      <SaveAlertDialog
        title="Edit Alert"
        alert={alert}
        show={showSaveAlertDialog}
        close={closeEditAlertDialog}
        handleSubmit={handleEditAlert}
        handleSaveAsNewSubmit={handleSaveAsNewAlert}
      />
    </div>
  );
}

export default AlertListItem;
